import {
  EQUIPMENT_RARITIES,
  itemDefinition,
  itemQualityRules,
  type ItemDefinition,
} from "../equipment.js";
import { LOOT_CHANCE_SCALE, type MonsterLootTable } from "@ninja/domain";

function validRarity(
  value: unknown,
): value is (typeof EQUIPMENT_RARITIES)[number] {
  return EQUIPMENT_RARITIES.includes(
    value as (typeof EQUIPMENT_RARITIES)[number],
  );
}

function assertAllowedQuality(
  item: ItemDefinition,
  rarity: (typeof EQUIPMENT_RARITIES)[number],
  tier: number,
  enhancementLevel: number,
  dropId: string,
): void {
  const quality = itemQualityRules(item);
  if (
    !quality.rarities.includes(rarity) ||
    tier < quality.minTier ||
    tier > quality.maxTier ||
    enhancementLevel < 0 ||
    enhancementLevel > quality.maxEnhancement ||
    (rarity === "common" && (tier !== 1 || enhancementLevel !== 0))
  )
    throw new TypeError(`${dropId} uses quality not supported by ${item.id}`);
}

export function parseMonsterLootTable(
  value: unknown,
  monsterId: string,
): MonsterLootTable {
  if (!value || typeof value !== "object")
    throw new TypeError(`${monsterId} has invalid loot table`);
  const table = value as Partial<MonsterLootTable>;
  if (
    typeof table.configId !== "string" ||
    !/^[a-z][a-z0-9_.-]{0,79}$/.test(table.configId) ||
    !Array.isArray(table.items) ||
    table.items.length > 100
  )
    throw new TypeError(`${monsterId} has invalid loot table identity`);

  const dropIds = new Set<string>();
  const items = table.items.map((value) => {
    if (!value || typeof value !== "object")
      throw new TypeError(`${monsterId} has malformed loot entry`);
    const entry = value as MonsterLootTable["items"][number];
    const item = itemDefinition(entry.itemId);
    if (
      !entry.dropId ||
      !/^[a-z][a-z0-9_.-]{0,99}$/.test(entry.dropId) ||
      dropIds.has(entry.dropId) ||
      !item ||
      !Number.isInteger(entry.chance) ||
      entry.chance < 0 ||
      entry.chance > LOOT_CHANCE_SCALE ||
      !Number.isInteger(entry.countMin) ||
      !Number.isInteger(entry.countMax) ||
      entry.countMin < 1 ||
      entry.countMax < entry.countMin ||
      entry.countMax > (item.category === "equipment" ? 1_000 : 1_000_000) ||
      (entry.rarity !== undefined && entry.rarityRoll !== undefined)
    )
      throw new TypeError(`${monsterId} has invalid loot entry`);
    dropIds.add(entry.dropId);

    const tier = entry.tier ?? 1;
    const enhancementLevel = entry.enhancementLevel ?? 0;
    if (!Number.isInteger(tier) || !Number.isInteger(enhancementLevel))
      throw new TypeError(`${entry.dropId} has invalid tier or enhancement`);

    if (entry.rarity !== undefined) {
      if (!validRarity(entry.rarity))
        throw new TypeError(`${entry.dropId} has an unknown rarity`);
      assertAllowedQuality(
        item,
        entry.rarity,
        tier,
        enhancementLevel,
        entry.dropId,
      );
    } else if (entry.rarityRoll !== undefined) {
      if (!Array.isArray(entry.rarityRoll) || entry.rarityRoll.length === 0)
        throw new TypeError(`${entry.dropId} has an invalid rarity roll`);
      let weightTotal = 0;
      const seenRarities = new Set<string>();
      for (const outcome of entry.rarityRoll) {
        if (
          !outcome ||
          !validRarity(outcome.rarity) ||
          !Number.isInteger(outcome.weight) ||
          outcome.weight <= 0 ||
          seenRarities.has(outcome.rarity)
        )
          throw new TypeError(`${entry.dropId} has an invalid rarity outcome`);
        seenRarities.add(outcome.rarity);
        weightTotal += outcome.weight;
        assertAllowedQuality(
          item,
          outcome.rarity,
          tier,
          enhancementLevel,
          entry.dropId,
        );
      }
      if (weightTotal !== LOOT_CHANCE_SCALE)
        throw new TypeError(
          `${entry.dropId} rarity weights must sum to ${LOOT_CHANCE_SCALE}`,
        );
    } else {
      assertAllowedQuality(
        item,
        "common",
        tier,
        enhancementLevel,
        entry.dropId,
      );
    }

    return entry;
  });

  return { configId: table.configId, items };
}
